import { addonKindsOf } from '@mcpanel/core';
import { panelIndex } from './panel-index';
import { serverIndex } from './server-index';
import type { SearchEntry, SearchResult, SearchRole, SearchServer } from './types';
export { panelIndex, serverIndex };

export function normalizeSearch(value: string): string {
  return value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[ァ-ヶ]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0x60));
}

function permitted(entry: SearchEntry, role: SearchRole): boolean {
  if (entry.permission === 'owner') return role === 'owner';
  return entry.permission !== 'operate' || role === 'owner' || role === 'admin';
}

export function availableEntries(server: SearchServer, role: SearchRole): SearchEntry[] {
  const kinds = addonKindsOf(server.software.family);
  return serverIndex.filter((entry) => {
    if (!permitted(entry, role)) return false;
    switch (entry.availability) {
      case 'mods':
        return kinds.mods;
      case 'plugins':
        return kinds.plugins;
      case 'addons':
        return kinds.mods || kinds.plugins;
      case 'client-template':
        return !kinds.mods;
      default:
        return true;
    }
  });
}

export function entryUrl(entry: SearchEntry, server?: SearchServer): string {
  const url = new URL(entry.path.replace(':id', encodeURIComponent(server?.id ?? '')), 'http://panel.local');
  if (entry.tab) url.searchParams.set('tab', entry.tab);
  if (entry.section) url.searchParams.set('section', entry.section);
  url.searchParams.set('focus', entry.focus);
  return url.pathname + url.search;
}

function score(entry: Pick<SearchEntry, 'name' | 'description' | 'keywords'>, words: string[]): number {
  const name = normalizeSearch(entry.name);
  const text = normalizeSearch(`${entry.name} ${entry.description} ${entry.keywords}`);
  if (!words.every((word) => text.includes(word))) return -1;
  return words.reduce((total, word) => total + (name.includes(word) ? 10 : 0), 0);
}

function matching(entries: SearchEntry[], query: string): SearchEntry[] {
  const words = normalizeSearch(query).trim().split(/\s+/).filter(Boolean);
  return entries
    .map((entry, order) => ({ entry, order, score: score(entry, words) }))
    .filter((item) => item.score >= 0)
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .map((item) => item.entry);
}

export function searchServer(server: SearchServer, role: SearchRole, query: string): SearchResult[] {
  return matching(availableEntries(server, role), query).map((entry) => ({
    id: `${server.id}:${entry.id}`,
    name: entry.name,
    description: entry.place ?? entry.description,
    url: entryUrl(entry, server),
    group: 'サーバーの設定',
  }));
}

export function searchAll(servers: SearchServer[], role: SearchRole, query: string): SearchResult[] {
  const panelEntries = matching(
    panelIndex.filter((entry) => permitted(entry, role)),
    query,
  );
  const panels: SearchResult[] = panelEntries.map((entry) => ({
    id: entry.id,
    name: entry.name,
    description: entry.description,
    url: entryUrl(entry),
    group: '画面・Panel の設定',
  }));
  const words = normalizeSearch(query).trim().split(/\s+/).filter(Boolean);
  const serverResults: SearchResult[] = servers
    .filter(
      (server) => score({ name: server.name, description: server.id, keywords: server.software.family }, words) >= 0,
    )
    .slice(0, 30)
    .map((server) => ({
      id: server.id,
      name: server.name,
      description: `${server.id} ・ ${server.software.family}`,
      url: `/servers/${encodeURIComponent(server.id)}`,
      group: 'サーバー',
    }));
  const settingItems = words.length
    ? servers
        .flatMap((server) =>
          availableEntries(server, role).map((entry) => ({
            server,
            entry,
            score: score(
              { ...entry, name: `${server.name} ${entry.name}`, keywords: `${server.id} ${entry.keywords}` },
              words,
            ),
          })),
        )
        .filter((item) => item.score >= 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 30)
    : [];
  const settings = settingItems.map(({ server, entry }): SearchResult => ({
    id: `${server.id}:${entry.id}`,
    name: `${server.name} › ${entry.place ?? entry.description} › ${entry.name}`,
    description: entry.description,
    url: entryUrl(entry, server),
    group: 'サーバーの設定',
  }));
  // 名前で一致したサーバーの設定が、キーワードだけで一致した画面より上に来るよう、
  // 一番よく一致した候補の点数でグループの順を決める（同点なら画面・Panel の設定を先に）
  const panelBest = panelEntries[0] ? score(panelEntries[0], words) : -1;
  const settingsBest = settingItems[0]?.score ?? -1;
  return settingsBest > panelBest
    ? [...serverResults, ...settings, ...panels]
    : [...panels, ...serverResults, ...settings];
}
